import {
    Button,
    CircularProgress,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField
} from '@material-ui/core';
import { Form, Formik } from 'formik';
import { formatMoney } from '../../../utils';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import { useNotify } from '../../../hooks/useNotify';

function parseSafeFloat(value) {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
}

function BookingItemPartialRefund({ maxAmount, bookingUserId, onSuccess, bookingItemId }) {
    const { notifyError, notifySuccess } = useNotify();

    const [balancesData, balancesLoading, balancesError] = useSwaggerClient(
        'balance',
        'getBalance',
        { userId: bookingUserId }
    );

    const validateRefundForm = values => {
        const errors = {};
        const vendorRefund = parseSafeFloat(values.vendorRefund);
        const agencyRefund = parseSafeFloat(values.agencyRefund);
        if (vendorRefund + agencyRefund > maxAmount) {
            errors.vendorRefund = 'Total refund exceeds the maximum allowed amount.';
            errors.agencyRefund = 'Total refund exceeds the maximum allowed amount.';
        }
        if (vendorRefund < 0) {
            errors.vendorRefund = 'Vendor refund cannot be negative.';
        }
        if (agencyRefund < 0) {
            errors.agencyRefund = 'Agency refund cannot be negative.';
        }
        if (vendorRefund === 0 && agencyRefund === 0) {
            errors.vendorRefund = 'At least one refund amount must be greater than zero.';
            errors.agencyRefund = 'At least one refund amount must be greater than zero.';
        }
        return errors;
    };
    return (
        <Formik
            initialValues={{
                vendorRefund: '0.00',
                agencyRefund: '0.00',
                note: '',
                forceToBalance: false
            }}
            validate={validateRefundForm}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
                try {
                    await doSwaggerCall('bookings', 'partialRefundBookingItem', {
                        bookingItemId,
                        partialRefundData: {
                            agency_refund: parseSafeFloat(values.agencyRefund),
                            vendor_refund: parseSafeFloat(values.vendorRefund),
                            forceToBalance: values.forceToBalance,
                            note: values.note
                        }
                    });
                    notifySuccess('Partial refund successful');
                    onSuccess();
                    resetForm();
                } catch (err) {
                    notifyError(err);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ values, setFieldValue, isSubmitting, isValid, errors, handleSubmit }) => (
                <Form>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell align="left" style={{ backgroundColor: '#f5f5f5' }}>
                                    <b>Refund</b>
                                </TableCell>
                                <TableCell align="right" style={{ backgroundColor: '#f5f5f5' }}>
                                    Total:{' '}
                                    <b>
                                        {formatMoney(
                                            parseSafeFloat(values.vendorRefund) +
                                                parseSafeFloat(values.agencyRefund)
                                        )}
                                    </b>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2} align="right">
                                    <TextField
                                        fullWidth
                                        label="Vendor refund"
                                        value={values?.vendorRefund}
                                        onChange={e =>
                                            setFieldValue('vendorRefund', e.target.value)
                                        }
                                        error={!!errors.vendorRefund}
                                        helperText={errors.vendorRefund}
                                        disabled={isSubmitting}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2} align="right">
                                    <TextField
                                        fullWidth
                                        label="Agency refund"
                                        error={!!errors.agencyRefund}
                                        helperText={errors.agencyRefund}
                                        value={values?.agencyRefund}
                                        onChange={e =>
                                            setFieldValue('agencyRefund', e.target.value)
                                        }
                                        disabled={isSubmitting}
                                    />
                                </TableCell>
                            </TableRow>
                            {balancesData?.balances?.length > 0 && (
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        {/* Force refund to balance option */}
                                        <InputLabel
                                            htmlFor={`forceto`}
                                            style={{ fontSize: '10px' }}
                                        >
                                            Refund to ...
                                        </InputLabel>
                                        <Select
                                            id={`forceto`}
                                            fullWidth
                                            label="Refund to balance"
                                            value={values?.forceToBalance}
                                            onChange={e =>
                                                setFieldValue('forceToBalance', e.target.value)
                                            }
                                            disabled={isSubmitting}
                                        >
                                            <MenuItem value={false}>
                                                original payment method
                                            </MenuItem>
                                            {balancesData.balances.map(balance => (
                                                <MenuItem key={balance.slug} value={balance.slug}>
                                                    "{balance.name}" balance
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell colSpan={3}>
                                    Note:
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        placeholder="Add a note for the refund"
                                        onChange={e => setFieldValue('note', e.target.value)}
                                        value={values?.note || ''}
                                        disabled={isSubmitting}
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={3} align="right">
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting || !isValid}
                                        fullWidth
                                        size="small"
                                        onClick={handleSubmit}
                                        startIcon={
                                            isSubmitting ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null
                                        }
                                    >
                                        {isSubmitting ? 'Processing refund...' : 'Start refund'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Form>
            )}
        </Formik>
    );
}

export default BookingItemPartialRefund;
